/**
 * Recurring costs. These create the pressure that makes earning matter.
 *
 * Balance intent: week one must be genuinely tight - buying the $60 mower out
 * of $100 should leave rent uncomfortably close - without ever being a dead
 * end, because courier gigs always exist. Missed rent becomes debt with a late
 * fee rather than a game over.
 */
import type { GameState } from '../state/GameState.js';
import type { EventBus } from '../core/events.js';
import type { FinanceSystem } from './FinanceSystem.js';
import { BALANCE } from '../data/balance.js';
import { getHousing, VEHICLES_BY_ID } from '../data/catalog.js';

export class ExpenseSystem {
  constructor(private readonly bus: EventBus) {}

  /** Charged once per week, on the rent day. */
  applyWeekly(state: GameState, finance: FinanceSystem): void {
    const week = state.time.week;

    if (state.time.dayOfWeek === BALANCE.expenses.rentDayOfWeek) {
      if (state.lastRentWeekCharged < week) {
        state.lastRentWeekCharged = week;
        const housing = getHousing(state.player.housingId);
        const paid = finance.charge(state, housing.weeklyRent, 'rent', `Rent - ${housing.name}`);
        this.bus.emit({
          type: 'expense.charged',
          name: 'Rent',
          amount: housing.weeklyRent,
          paid,
        });
        if (!paid) {
          state.statistics.rentMissed += 1;
          const fee = Math.round(housing.weeklyRent * BALANCE.expenses.lateFeeRate);
          state.finance.debt += fee;
          this.bus.emit({
            type: 'notice',
            level: 'bad',
            message: 'You could not cover rent. A late fee has been added to your debt.',
          });
        }
      }

      if (state.lastPhoneWeekCharged < week) {
        state.lastPhoneWeekCharged = week;
        const paid = finance.charge(
          state,
          BALANCE.expenses.phoneWeekly,
          'phone',
          'Phone plan',
        );
        this.bus.emit({
          type: 'expense.charged',
          name: 'Phone',
          amount: BALANCE.expenses.phoneWeekly,
          paid,
        });
      }

      if (state.lastUpkeepWeekCharged < week) {
        state.lastUpkeepWeekCharged = week;
        const items = state.business.equipment.length + state.business.crewKits;
        const upkeep =
          items * BALANCE.expenses.equipmentUpkeepWeekly +
          (state.business.registered ? BALANCE.business.licenceWeekly : 0);
        if (upkeep > 0) {
          const paid = finance.charge(state, upkeep, 'upkeep', 'Equipment & licence upkeep');
          this.bus.emit({
            type: 'expense.charged',
            name: 'Upkeep',
            amount: upkeep,
            paid,
          });
        }
      }

      if (state.lastInterestWeekCharged < week) {
        state.lastInterestWeekCharged = week;
        if (state.finance.debt > 0) {
          const interest = Math.round(state.finance.debt * BALANCE.debt.weeklyInterest);
          state.finance.debt += interest;
          this.bus.emit({
            type: 'notice',
            level: 'warn',
            message: `Interest of ${(interest / 100).toFixed(2)} added to your debt.`,
          });
        }
      }
    }

    if (state.time.dayOfWeek === BALANCE.employees.payDayOfWeek) {
      // Payroll itself is run by EmployeeSystem; this is only the marker.
      if (state.lastWagesWeekPaid < week) {
        state.lastWagesWeekPaid = week;
      }
    }
  }

  /** Charged every day. */
  applyDaily(state: GameState, finance: FinanceSystem): void {
    if (state.player.vehicleId) {
      const v = VEHICLES_BY_ID.get(state.player.vehicleId);
      if (v && v.upkeepDaily > 0) {
        finance.charge(state, v.upkeepDaily, 'upkeep', `${v.name} running costs`);
      }
    }
  }

  /** What the player owes every week, shown on the phone's finance screen. */
  weeklyCommitment(state: GameState): number {
    const housing = getHousing(state.player.housingId);
    const items = state.business.equipment.length + state.business.crewKits;
    return (
      housing.weeklyRent +
      BALANCE.expenses.phoneWeekly +
      items * BALANCE.expenses.equipmentUpkeepWeekly +
      (state.business.registered ? BALANCE.business.licenceWeekly : 0) +
      (state.player.vehicleId ? (VEHICLES_BY_ID.get(state.player.vehicleId)?.upkeepDaily ?? 0) * 7 : 0)
    );
  }
}
